/**
 * m3 echo Frontend - Kostenpanel der Benchmark-Aufstellung (Admin)
 *
 * Zeigt Kostenvorschau (aktuell vs. Entwurf) und Erfassungsquote je Modell.
 */

import React from 'react';
import { Alert, Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import type { BenchmarkCostPreview } from '../../types/api';

export const LOW_COVERAGE_THRESHOLD = 0.8;

const COVERAGE_TOOLTIP =
  'Erfassungsquote: erfasste Token-Zeilen je Ergebnis; niedrige Werte durch Erfassungslücken oder fehlgeschlagene Aufrufe';

interface Props {
  preview: BenchmarkCostPreview | null;
  loading: boolean;
  error: string;
  modelLabel: (code: string) => string;
}

const formatMoney = (value: number | null | undefined, currency: string): string => {
  if (value === null || value === undefined) return '–';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

const formatDelta = (value: number, currency: string): string =>
  `${value > 0 ? '+' : ''}${formatMoney(value, currency)}`;

const formatCoverage = (coverage: number | null): string =>
  coverage === null ? '–' : `${(coverage * 100).toLocaleString('de-DE', { maximumFractionDigits: 0 })} %`;

const BenchmarkCostPanel: React.FC<Props> = ({ preview, loading, error, modelLabel }) => {
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  if (!preview) {
    return loading ? <CircularProgress size={20} sx={{ mb: 2 }} /> : null;
  }

  const codes = Object.keys(preview.models);

  return (
    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1">
          Aktuell {formatMoney(preview.current_total, preview.currency)} → Entwurf{' '}
          {formatMoney(preview.draft_total, preview.currency)} (Δ{' '}
          {formatDelta(preview.delta, preview.currency)})
        </Typography>
        {loading && <CircularProgress size={16} />}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {preview.label}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {codes.map((code) => {
          const m = preview.models[code];
          const low = m.coverage !== null && m.coverage < LOW_COVERAGE_THRESHOLD;
          const inDraft = preview.draft.includes(code);
          const inCurrent = preview.current.includes(code);
          return (
            <Box
              key={code}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
            >
              <Typography variant="body2" sx={{ minWidth: 160 }}>
                {modelLabel(code)}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={inDraft && inCurrent ? 'bleibt' : inDraft ? 'neu' : 'entfällt'}
              />
              <Chip
                size="small"
                label={m.status}
                color={m.status === 'erfasst' ? 'success' : m.status === 'geschätzt' ? 'info' : 'default'}
              />
              <Tooltip title={COVERAGE_TOOLTIP}>
                <Typography variant="caption" color="text.secondary">
                  Erfassungsquote {formatCoverage(m.coverage)}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {formatMoney(m.cost_per_month, preview.currency)} / Monat
              </Typography>
              {low && <Chip size="small" color="warning" label="Kosten vermutlich unterschätzt" />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BenchmarkCostPanel;
